import { useState } from 'react';
import { useQueries } from 'react-query';
import { useSelector } from 'react-redux';
import { getAsmAssetPaged, getCdnList, getWafList } from 'services/asm-service';
import { useAuthTenant } from 'store/auth';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { Shimmer } from 'Components/PageLoader/styles';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const FlexRow = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-block: 16px;
  margin-inline: auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 20px;
`;

const CardTitle = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin-bottom: 16px;
`;

const TH_STYLE = {
  fontFamily: 'Maven Pro, sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--vo-slate)',
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  padding: '11px 14px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  padding: '12px 14px',
  fontFamily: 'Lato, sans-serif',
  fontSize: '13px',
  color: 'var(--vo-ink)',
  verticalAlign: 'middle',
};

function HBar({ label, qt, maxQt, color, countColor }) {
  const pct = maxQt > 0 ? Math.max(2, (qt / maxQt) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <span style={{ width: '90px', fontSize: '12px', fontFamily: 'Maven Pro, sans-serif', color: 'var(--vo-slate)', flexShrink: 0, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, background: 'var(--vo-mist)', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '3px' }} />
      </div>
      <span style={{ fontSize: '12px', fontFamily: 'Maven Pro, sans-serif', fontWeight: 600, color: countColor || color, minWidth: '24px' }}>{qt}</span>
    </div>
  );
}

function cellVal(row, id) {
  const v = row[id];
  if (Array.isArray(v)) return v.join(', ');
  return v ?? '—';
}

export const TabAsset = () => {
  const { t } = useTranslation(['asm']);
  const [page, setPage] = useState(1);
  const selectedTenant = useSelector(useAuthTenant);

  const queries = useQueries([
    {
      queryKey: ['getCdnList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getCdnList(selectedTenant.slug);
        if (response?.data?.cdns) return response.data.cdns;
        return [];
      },
    },
    {
      queryKey: ['getWafList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getWafList(selectedTenant.slug);
        if (response?.data?.wafs) return response.data.wafs;
        return [];
      },
    },
    {
      queryKey: ['getAsmAssetPaged', page],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAsmAssetPaged(selectedTenant.slug, page, 6);
        if (result?.data?.data.length > 0) return result.data;
        return { data: [], page, totalPages: 0 };
      },
    },
  ]);

  const [
    { data: cdns, isLoading: cdnLoading },
    { data: wafs, isLoading: wafLoading },
    { data: pagedAsmAsset, isLoading: asmAssetLoading },
  ] = queries;

  const cdnMax = (cdns || []).reduce((m, d) => Math.max(m, d.qt || 0), 0);
  const wafMax = (wafs || []).reduce((m, d) => Math.max(m, d.qt || 0), 0);

  const columns = [
    { id: 'asset_type', label: t('asm:tabAsset.table.type') },
    { id: 'asset', label: t('asm:tabAsset.table.asset') },
    { id: 'ports', label: t('asm:tabAsset.table.ports') },
    { id: 'cdn_names', label: t('asm:tabAsset.table.cdn') },
    { id: 'technologies', label: t('asm:tabAsset.table.technologies') },
    { id: 'waf', label: t('asm:tabAsset.table.waf') },
    { id: 'webserver', label: t('asm:tabAsset.table.webserver') },
    { id: 'source', label: t('asm:tabAsset.table.source') },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }} data-tour="charts-container">
        <Card data-tour="cdn-chart">
          <CardTitle>{t('asm:tabAsset.chartCdn')}</CardTitle>
          {cdnLoading
            ? <Shimmer style={{ height: '150px', width: '100%' }} />
            : (cdns || []).map(d => <HBar key={d.name} label={d.name} qt={d.qt} maxQt={cdnMax} color="var(--vo-capri)" countColor="var(--vo-celeste)" />)
          }
        </Card>
        <Card data-tour="waf-chart">
          <CardTitle>{t('asm:tabAsset.chartWaf')}</CardTitle>
          {wafLoading
            ? <Shimmer style={{ height: '150px', width: '100%' }} />
            : (wafs || []).map(d => <HBar key={d.name} label={d.name} qt={d.qt} maxQt={wafMax} color="var(--vo-celeste)" countColor="var(--vo-cobalto)" />)
          }
        </Card>
      </div>
      {asmAssetLoading && <PageLoaderList />}
      {!asmAssetLoading && (
        <>
          <div style={{ background: 'white', borderRadius: '14px', boxShadow: '0 1px 3px var(--vo-escuro-8)', overflow: 'auto' }} data-tour="assets-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
              <thead>
                <tr style={{ background: 'var(--vo-mist)' }}>
                  {columns.map(col => (
                    <th key={col.id} style={TH_STYLE}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(pagedAsmAsset?.data || []).length === 0 && (
                  <tr>
                    <td colSpan={columns.length} style={{ ...TD_STYLE, textAlign: 'center', color: 'var(--vo-steel)', padding: '24px' }}>—</td>
                  </tr>
                )}
                {(pagedAsmAsset?.data || []).map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--vo-mist)' }}>
                    {columns.map(col => {
                      if (col.id === 'asset_type') {
                        return (
                          <td key={col.id} style={TD_STYLE}>
                            {row.asset_type
                              ? <span style={{ fontFamily: 'Maven Pro, sans-serif', fontSize: '11px', fontWeight: 600, color: 'var(--vo-cobalto)', background: 'var(--vo-cobalto-8)', padding: '2px 8px', borderRadius: '10px', display: 'inline-block' }}>{row.asset_type}</span>
                              : '—'
                            }
                          </td>
                        );
                      }
                      if (col.id === 'asset') {
                        return (
                          <td key={col.id} style={{ ...TD_STYLE, fontFamily: 'Maven Pro, sans-serif', fontWeight: 600 }}>{row.asset ?? '—'}</td>
                        );
                      }
                      return <td key={col.id} style={TD_STYLE}>{cellVal(row, col.id)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagedAsmAsset?.totalPages > 0 && (
            <FlexRow data-tour="pagination">
              <DefaultPagination
                count={pagedAsmAsset.totalPages}
                onChange={(_, selectedPage) => setPage(selectedPage)}
                color="primary"
                page={page}
                showFirstButton
                showLastButton
              />
            </FlexRow>
          )}
        </>
      )}
    </>
  );
};
